import React, { useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import LocationInput from "../objects/LocationInput.tsx";
import AgentInput from "../objects/AgentInput.tsx";

/** Bumped when this UI changes — DevTools: root has this data attribute when the new panel is live. */
export const PARENT_FILTERS_LAYOUT_ID = "filters-v4-accordion-desktop-grid";

/** Set at CI build time (GitHub Actions → REACT_APP_RELEASE). If missing on production, you’re on an old bundle or local dev. */
const RELEASE_LABEL =
  typeof process.env.REACT_APP_RELEASE === "string" && process.env.REACT_APP_RELEASE.length >= 7
    ? process.env.REACT_APP_RELEASE.slice(0, 7)
    : null;

export interface ParentListFilters {
  name: string;
  booking_id: string;
  date_from: string;
  date_to: string;
  location: number | null;
  agent: number | null;
}

export interface ParentFiltersPanelProps {
  filters: ParentListFilters;
  onNameChange: (value: string) => void;
  onLocationChange: (value: { id: number } | null) => void;
  onAgentChange: (value: number | null) => void;
  onDateFieldChange: (field: "date_from" | "date_to", value: string) => void;
  onNormalizeDateRange: () => void;
  onNextTwoWeeks: () => void;
  onReset: () => void;
}

function useHasActiveFilters(f: ParentListFilters): boolean {
  return useMemo(
    () =>
      Boolean(
        f.name.trim() ||
          f.booking_id.trim() ||
          f.date_from ||
          f.date_to ||
          f.location != null ||
          (f.agent != null && f.agent > 0)
      ),
    [f]
  );
}

const dateSlotProps = { inputLabel: { shrink: true } } as const;

const FilterFields: React.FC<{
  filters: ParentListFilters;
  onNameChange: (v: string) => void;
  onLocationChange: (v: { id: number } | null) => void;
  onAgentChange: (v: number | null) => void;
  onDateFieldChange: (field: "date_from" | "date_to", v: string) => void;
  onNormalizeDateRange: () => void;
  onNextTwoWeeks: () => void;
  onReset: () => void;
  layout: "stack" | "desktop-grid";
  hasActive: boolean;
}> = ({
  filters,
  onNameChange,
  onLocationChange,
  onAgentChange,
  onDateFieldChange,
  onNormalizeDateRange,
  onNextTwoWeeks,
  onReset,
  layout,
  hasActive,
}) => {
  const nameField = (
    <TextField
      fullWidth
      size="small"
      label="Name or reference"
      name="name"
      placeholder="Type to filter…"
      value={filters.name}
      onChange={(e) => onNameChange(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" color="action" />
          </InputAdornment>
        ),
      }}
    />
  );

  const locationField = (
    <LocationInput size="small" value={filters.location} onChange={onLocationChange} />
  );

  const agentField = (
    <AgentInput
      size="small"
      value={filters.agent}
      onChange={(id) => onAgentChange(id === 0 ? null : id)}
    />
  );

  const dateFrom = (
    <TextField
      fullWidth
      size="small"
      label="From"
      name="date_from"
      type="date"
      value={filters.date_from}
      onChange={(e) => onDateFieldChange("date_from", e.target.value)}
      onBlur={onNormalizeDateRange}
      slotProps={dateSlotProps}
    />
  );

  const dateTo = (
    <TextField
      fullWidth
      size="small"
      label="To"
      name="date_to"
      type="date"
      value={filters.date_to}
      onChange={(e) => onDateFieldChange("date_to", e.target.value)}
      onBlur={onNormalizeDateRange}
      slotProps={dateSlotProps}
    />
  );

  const shortcutBtn = (
    <Button variant="contained" size="medium" onClick={onNextTwoWeeks} sx={{ whiteSpace: "nowrap", px: 2 }}>
      Next 14 days
    </Button>
  );

  const clearBtn = (
    <Button variant="outlined" color="inherit" size="medium" onClick={onReset} disabled={!hasActive}>
      Clear
    </Button>
  );

  if (layout === "stack") {
    return (
      <Stack spacing={2}>
        {nameField}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>{locationField}</Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>{agentField}</Box>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          Tour date (optional)
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-end" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>{dateFrom}</Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>{dateTo}</Box>
          <Stack direction="row" spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
            {shortcutBtn}
            {clearBtn}
          </Stack>
        </Stack>
      </Stack>
    );
  }

  /* Desktop: one explicit grid row — six tracks, no wrapping */
  return (
    <Box
      sx={{
        display: "grid",
        width: "100%",
        boxSizing: "border-box",
        gap: 2,
        alignItems: "end",
        gridTemplateColumns:
          "minmax(0, 2.1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, min(160px, 14vw)) minmax(0, min(160px, 14vw)) auto",
        gridAutoRows: "auto",
      }}
    >
      <Box sx={{ minWidth: 0 }}>{nameField}</Box>
      <Box sx={{ minWidth: 0, "& .MuiAutocomplete-root": { minWidth: 0 } }}>{locationField}</Box>
      <Box sx={{ minWidth: 0, "& .MuiAutocomplete-root": { minWidth: 0 } }}>{agentField}</Box>
      <Box sx={{ minWidth: 0 }}>{dateFrom}</Box>
      <Box sx={{ minWidth: 0 }}>{dateTo}</Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, pb: 0.25 }}>
        {shortcutBtn}
      </Stack>
    </Box>
  );
};

const ParentFiltersPanel: React.FC<ParentFiltersPanelProps> = ({
  filters,
  onNameChange,
  onLocationChange,
  onAgentChange,
  onDateFieldChange,
  onNormalizeDateRange,
  onNextTwoWeeks,
  onReset,
}) => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const hasActive = useHasActiveFilters(filters);

  const help = (
    <Tooltip
      title="Empty dates = all tour dates. Set From/To to limit the list. “Next 14 days” fills today through 13 days ahead."
      arrow
      enterTouchDelay={0}
    >
      <IconButton size="small" aria-label="How filters work" sx={{ ml: -0.5 }}>
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  if (isCompact) {
    return (
      <Paper
        data-filter-layout={PARENT_FILTERS_LAYOUT_ID}
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: "100%",
          mb: 2.5,
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          overflow: "hidden",
          bgcolor: (t) => (t.palette.mode === "dark" ? alpha(t.palette.common.white, 0.04) : alpha(t.palette.primary.main, 0.04)),
        }}
      >
        <Accordion
          defaultExpanded
          disableGutters
          elevation={0}
          sx={{
            "&:before": { display: "none" },
            bgcolor: "transparent",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              px: 2,
              py: 1,
              minHeight: 52,
              "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1, my: 0.5 },
            }}
          >
            <TuneIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={700}>
              Search &amp; filters
            </Typography>
            {hasActive && <Chip size="small" label="Active" color="primary" variant="outlined" />}
            {help}
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
            <FilterFields
              filters={filters}
              onNameChange={onNameChange}
              onLocationChange={onLocationChange}
              onAgentChange={onAgentChange}
              onDateFieldChange={onDateFieldChange}
              onNormalizeDateRange={onNormalizeDateRange}
              onNextTwoWeeks={onNextTwoWeeks}
              onReset={onReset}
              layout="stack"
              hasActive={hasActive}
            />
          </AccordionDetails>
        </Accordion>
        {RELEASE_LABEL && (
          <Typography
            variant="caption"
            sx={{ display: "block", px: 2, pb: 1, fontFamily: "monospace", color: "text.disabled", textAlign: "center" }}
          >
            Build {RELEASE_LABEL}
          </Typography>
        )}
      </Paper>
    );
  }

  return (
    <Paper
      data-filter-layout={PARENT_FILTERS_LAYOUT_ID}
      elevation={0}
      sx={{
        width: "100%",
        maxWidth: "100%",
        mb: 2.5,
        borderRadius: 2,
        border: 1,
        borderColor: "divider",
        overflow: "hidden",
        bgcolor: (t) => (t.palette.mode === "dark" ? alpha(t.palette.common.white, 0.04) : alpha(t.palette.primary.main, 0.04)),
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          bgcolor: (t) => (t.palette.mode === "dark" ? alpha(t.palette.common.black, 0.2) : alpha(t.palette.common.white, 0.65)),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap>
          <TuneIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={700}>
            Find parent tours
          </Typography>
          {help}
          {hasActive && <Chip size="small" label="Filters on" color="primary" variant="outlined" sx={{ ml: 1 }} />}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
          {RELEASE_LABEL && (
            <Tooltip title={`Frontend build ${process.env.REACT_APP_RELEASE}`}>
              <Typography
                component="span"
                variant="caption"
                sx={{ fontFamily: "monospace", color: "text.disabled", cursor: "default" }}
              >
                Build {RELEASE_LABEL}
              </Typography>
            </Tooltip>
          )}
          <Button size="small" variant="text" onClick={onReset} disabled={!hasActive} sx={{ fontWeight: 600 }}>
            Clear all
          </Button>
        </Stack>
      </Box>
      <Box sx={{ px: 2, py: 2, overflowX: "auto" }}>
        <FilterFields
          filters={filters}
          onNameChange={onNameChange}
          onLocationChange={onLocationChange}
          onAgentChange={onAgentChange}
          onDateFieldChange={onDateFieldChange}
          onNormalizeDateRange={onNormalizeDateRange}
          onNextTwoWeeks={onNextTwoWeeks}
          onReset={onReset}
          layout="desktop-grid"
          hasActive={hasActive}
        />
      </Box>
    </Paper>
  );
};

export default ParentFiltersPanel;
